import React, { useMemo, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Platform } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';

export interface DesktopDropdownOption {
  label: string;
  value: string;
}

interface DesktopDropdownSelectorProps {
  label: string;
  selectedValue?: string | number | null;
  options: DesktopDropdownOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
}

export const DesktopDropdownSelector: React.FC<DesktopDropdownSelectorProps> = ({
  label,
  selectedValue,
  options,
  onChange,
  placeholder = 'Select',
  required,
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);
  const anchorRef = useRef<any>(null);

  const selectedLabel = useMemo(() => {
    const found = options.find((o) => String(o.value) === String(selectedValue ?? ''));
    return found?.label ?? '';
  }, [options, selectedValue]);

  const onPress = () => {
    if (Platform.OS === 'web' && anchorRef.current?.getBoundingClientRect) {
      const rect = anchorRef.current.getBoundingClientRect();
      setAnchor({ x: rect.left, y: rect.top, w: rect.width, h: rect.height });
      setIsOpen(true);
      return;
    }
    // Fallback: open anchored dropdown at a synthetic anchor near top-left
    setAnchor({ x: 24, y: 120, w: 320, h: 44 });
    setIsOpen(true);
  };

  return (
    <View style={styles.container}>
      <Text style={styles.label}>
        {label} {required ? <Text style={styles.required}>*</Text> : null}
      </Text>
      <TouchableOpacity
        ref={anchorRef}
        style={[styles.selector, error && styles.selectorError]}
        onPress={onPress}
      >
        <Text
          numberOfLines={1}
          style={[styles.selectedText, !selectedLabel && styles.placeholderText]}
        >
          {selectedLabel || placeholder}
        </Text>
        <SVG_COMPONENTS.CHEVRON_DOWN width={18} height={18} color={ADMIN_COLORS.TEXT_SECONDARY} />
      </TouchableOpacity>
      {error ? <Text style={styles.errorText}>{error}</Text> : null}

      <AnchoredDropdown
        isOpen={isOpen}
        anchor={anchor}
        width={anchor?.w ?? 320}
        maxHeight={320}
        onRequestClose={() => setIsOpen(false)}
      >
        <ScrollView style={{ maxHeight: 320 }} contentContainerStyle={{ paddingVertical: 6 }}>
          {options.map((opt) => {
            const active = String(opt.value) === String(selectedValue ?? '');
            return (
              <TouchableOpacity
                key={`opt-${opt.value}`}
                style={[styles.optionRow, active && styles.optionRowActive]}
                onPress={() => {
                  onChange(String(opt.value));
                  setIsOpen(false);
                }}
              >
                <Text style={[styles.optionText, active && styles.optionTextActive]}>{opt.label}</Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </AnchoredDropdown>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginBottom: 8,
  },
  required: {
    color: ADMIN_COLORS.ERROR,
  },
  selector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    paddingHorizontal: 12,
    paddingVertical: 12,
    minHeight: 48,
  },
  selectorError: {
    borderColor: ADMIN_COLORS.ERROR,
  },
  selectedText: {
    flex: 1,
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_PRIMARY,
    marginRight: 8,
  },
  placeholderText: {
    color: ADMIN_COLORS.TEXT_TERTIARY,
  },
  errorText: {
    fontSize: 14,
    color: ADMIN_COLORS.ERROR,
    marginTop: 4,
  },
  optionRow: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  optionRowActive: {
    backgroundColor: `${ADMIN_COLORS.PRIMARY}10`,
  },
  optionText: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
  optionTextActive: {
    color: ADMIN_COLORS.PRIMARY,
    fontWeight: '600',
  },
});


